import { EChartsOption } from 'echarts';

import type { EventBehaviorTimeseriesPoint, ModelOutputRecord } from '@/features/forecasts/forecasts.api.types';
import { formatIsoDateMd } from '@/utils/date';

export type ForecastYAxisConfig = {
  min?: number;
  max?: number;
  interval?: number;
  splitNumber?: number;
};

export type ForecastProps = {
  actuals?: EventBehaviorTimeseriesPoint[];
  dates?: string[];
  forecast?: ModelOutputRecord[];
  yAxis?: ForecastYAxisConfig;
};

export type HistoryYAxisConfig = ForecastYAxisConfig & {
  name?: string;
  nameLocation?: 'start' | 'middle' | 'end';
  nameGap?: number;
};

export const buildCategoryXAxis = (timestamps: string[]) => ({
  type: 'category' as const,
  data: timestamps,
  axisLabel: {
    rotate: 45,
    fontSize: 12,
    formatter: (value: string) => formatIsoDateMd(value),
  },
});

export function computeYAxis(actuals: EventBehaviorTimeseriesPoint[], forecast: ModelOutputRecord[]) {
  const values = [
    ...actuals.map((d) => d?.quantity ?? 0),
    ...forecast.map((d) => d?.target_output?.val_next_quantity ?? d?.target_output?.mean ?? 0),
  ].filter((v) => v > 0);

  if (values.length === 0) {
    return {};
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const yMin = Math.floor(min * 0.9);
  const yMax = Math.ceil(max * 1.1);
  const range = yMax - yMin || 1;
  const tickCount = 5;
  const rawInterval = range / tickCount;
  const magnitude = 10 ** Math.floor(Math.log10(rawInterval));
  const interval = Math.ceil(rawInterval / magnitude) * magnitude;
  const alignedMin = Math.floor(yMin / interval) * interval;
  const alignedMax = Math.ceil(yMax / interval) * interval;

  return {
    min: alignedMin,
    max: alignedMax,
    interval,
    splitNumber: tickCount,
  } as ForecastYAxisConfig;
}

export function createNoDataOption(message: string) {
  return {
    graphic: {
      type: 'text',
      left: 'center',
      top: 'middle',
      style: {
        text: message,
        fontSize: 32,
        fontWeight: 600,
      },
    },
  } satisfies EChartsOption;
}
